import { PLAYER_H, PLAYER_W, WORLD_H, WORLD_W, type LevelDef, type PlayerState, type Point, type Rect, type WorldState } from '../../shared/types';
import { getPlatforms, isGateOpen, isHazardActive } from '../../shared/simulation';
import { LANTERN_COLORS } from './lantern';
import { AMBER, INK, PAPER, TAU, clamp, disc, line, rounded, text } from './paint';
import { drawKey } from './world';
import { DANGER_SURFACE, dangerZones, screenRect, toScreen, type CameraView } from './camera';
import { describeHazard } from './hazards';

/** A deadly surface this close (world px) to the local courier earns an edge cue while out of frame. */
export const DANGER_CUE_RANGE = 520;
/** An on-screen callout other than the local courier's tag stays this close (CSS px, leader length) to what it labels, or hides. */
export const LEADER_MAX_PX = 80;
/** Bodies drawn at most this wide (CSS px) make a dense frame: the whole-trail overview, and any frame that small. */
export const DENSE_BODY_PX = 34;
/** Screen-space callouts drawn per frame in a dense frame, at most (the lowest priority is dropped first).
 * Set from the trails' own persistent label load at eight couriers: Cargo Company and All Aglow carry 14
 * (plates, doors, bridges, cargo, hazard, door count, key, signs, the YOU tag), A Little Higher 17. Sixteen keeps
 * every persistent label plus one collapsed contributor chip per plate on every trail but the heaviest, where it
 * trims the lowest sign card; the collapse of per-body chips and floating badges is what removes the pile-up. */
export const DENSE_LABEL_BUDGET = 16;
/** The fixed-size identity badge drawn on each body in a dense frame (radius, CSS px). */
const DENSE_BADGE_RADIUS = 7.2;

export interface AnnotationRecord {
  id: string;
  kind: 'player' | 'local' | 'switch' | 'circuit' | 'cargo' | 'exit' | 'key' | 'hazard' | 'sign' | 'danger' | 'helped' | 'hud';
  label: string;
  rect: Rect;
  anchor: Point;
  fontSize: number;
  badgeSize: number;
  offscreen: boolean;
  placement?: 'body' | 'callout';
  /** Drawn leader length in CSS px (label centre to the leader's end); 0 when no leader is drawn. */
  leaderPx: number;
}

/** Per-frame label accounting (read-only diagnostics). */
export interface LabelStats {
  /** Bodies were drawn at ≤ DENSE_BODY_PX: badges on bodies, collapsed contributor chips, no leaders, a callout budget. */
  dense: boolean;
  /** Screen-space callouts drawn (labels other than badges placed on bodies and the HUD chip). */
  callouts: number;
  /** Identity badges drawn on bodies (dense frames and the close Team view). */
  bodyBadges: number;
  /** Callouts wanted this frame but dropped by the dense budget (never by lack of space alone). */
  droppedByBudget: number;
  /** Callouts wanted this frame but not drawn for lack of a clear, near slot. */
  droppedBySpace: number;
  budget: number | null;
}

/** A contributor chip requested by the cooperative beat: drawn near that courier's body. A dense frame
 * collapses the chips of one plate into a single chip over the bodies it credits. */
export interface HelpedChip { playerId: string; label: string; alpha: number; plateId?: string; letter?: string }

/** The persistent counter chip in the HUD corner (authoritative `world.deaths`). */
export interface HudChip { id: string; label: string }
const HUD_CHIP_HEIGHT = 26;
const HUD_FONT = '800 12px "Trebuchet MS", "Avenir Next", system-ui, sans-serif';

interface Cue {
  id: string;
  kind: AnnotationRecord['kind'];
  label: string;
  anchor: Point;
  body?: Rect;
  width: number;
  height: number;
  fill: string;
  ink?: string;
  local?: boolean;
  badge?: boolean;
  offscreen?: boolean;
  edge?: 'left' | 'right' | 'top' | 'bottom';
  below?: boolean;
  optional?: boolean;
  icon?: 'lock' | 'shield';
  danger?: 'water' | 'bramble';
  alpha?: number;
  /** A sign wrapped onto rows (portrait Team view); `label` stays the full text. */
  lines?: string[];
}

/** Two rows, split at the word gap nearest the middle of the text. */
function wrapSign(ctx: CanvasRenderingContext2D, textValue: string): string[] {
  const words = textValue.split(/\s+/);
  if (words.length < 2) return [textValue];
  let best = 1, bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const a = ctx.measureText(words.slice(0, i).join(' ')).width, b = ctx.measureText(words.slice(i).join(' ')).width;
    if (Math.abs(a - b) < bestDiff) { bestDiff = Math.abs(a - b); best = i; }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
}

const intersects = (a: Rect, b: Rect, gap = 0) => a.x < b.x + b.w + gap && a.x + a.w + gap > b.x && a.y < b.y + b.h + gap && a.y + a.h + gap > b.y;
const inflate = (rect: Rect, amount: number): Rect => ({ x: rect.x - amount, y: rect.y - amount, w: rect.w + amount * 2, h: rect.h + amount * 2 });
const visible = (rect: Rect, view: CameraView) => intersects(rect, { x: 0, y: 0, w: view.width, h: view.height });

/** The screen rectangle the world actually occupies: the canvas, less any letterbox inset. */
export function visibleFrame(view: CameraView): Rect {
  const worldX = view.offsetX - view.x * view.scale, worldY = view.offsetY - view.y * view.scale;
  const x0 = Math.max(0, worldX), y0 = Math.max(0, worldY);
  const x1 = Math.min(view.width, worldX + WORLD_W * view.scale), y1 = Math.min(view.height, worldY + WORLD_H * view.scale);
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}

/** The share of a screen rectangle's area lying inside the frame (0 = fully outside, 1 = fully inside). */
export function shownFraction(rect: Rect, frame: Rect): number {
  const w = Math.min(rect.x + rect.w, frame.x + frame.w) - Math.max(rect.x, frame.x);
  const h = Math.min(rect.y + rect.h, frame.y + frame.h) - Math.max(rect.y, frame.y);
  if (w <= 0 || h <= 0 || rect.w <= 0 || rect.h <= 0) return 0;
  return (w * h) / (rect.w * rect.h);
}
/** A teammate whose body is at least this much inside the frame is on screen: it gets its badge, never an edge arrow. */
export const ON_SCREEN_FRACTION = 0.4;

function leaderEnd(cue: Cue, rect: Rect): Point {
  const point = { ...cue.anchor };
  if (!cue.body) return point;
  const body = cue.body;
  const dx = rect.x + rect.w / 2 - point.x;
  const dy = rect.y + rect.h / 2 - point.y;
  if (Math.abs(dx / body.w) > Math.abs(dy / body.h)) {
    point.x = dx < 0 ? body.x - 1 : body.x + body.w + 1;
    point.y = clamp(rect.y + rect.h / 2, body.y + 3, body.y + body.h - 3);
  } else {
    point.y = dy < 0 ? body.y - 1 : body.y + body.h + 1;
    point.x = clamp(rect.x + rect.w / 2, body.x + 3, body.x + body.w - 3);
  }
  return point;
}

/** Mirrors paint(): on-screen callouts other than signs and contributor chips draw a leader from the label centre.
 * A dense frame draws no leaders at all (a 30-px body under a tether reads as a diagram, not a courier). */
const hasLeader = (cue: Cue, dense = false) => !dense && cue.kind !== 'sign' && cue.kind !== 'helped' && !cue.offscreen;
function leaderLength(cue: Cue, rect: Rect): number {
  const end = leaderEnd(cue, rect);
  return Math.hypot(rect.x + rect.w / 2 - end.x, rect.y + rect.h / 2 - end.y);
}

function crossesBody(from: Point, to: Point, body: Rect): boolean {
  let low = 0, high = 1;
  for (const axis of ['x', 'y'] as const) {
    const delta = to[axis] - from[axis];
    const min = body[axis] + 0.7;
    const max = body[axis] + (axis === 'x' ? body.w : body.h) - 0.7;
    if (Math.abs(delta) < 0.001) { if (from[axis] <= min || from[axis] >= max) return false; }
    else {
      const a = (min - from[axis]) / delta;
      const b = (max - from[axis]) / delta;
      low = Math.max(low, Math.min(a, b));
      high = Math.min(high, Math.max(a, b));
      if (low >= high) return false;
    }
  }
  return low < high && high > 0 && low < 1;
}

function edgePoint(point: Point, view: CameraView): { point: Point; edge: Cue['edge']; arrow: string } {
  const dx = point.x - view.width / 2;
  const dy = point.y - view.height / 2;
  if (Math.abs(dx / view.width) >= Math.abs(dy / view.height)) {
    return { point: { x: dx < 0 ? 7 : view.width - 7, y: clamp(point.y, 23, view.height - 23) }, edge: dx < 0 ? 'left' : 'right', arrow: dx < 0 ? '←' : '→' };
  }
  return { point: { x: clamp(point.x, 23, view.width - 23), y: dy < 0 ? 7 : view.height - 7 }, edge: dy < 0 ? 'top' : 'bottom', arrow: dy < 0 ? '↑' : '↓' };
}

function shield(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - size);
  ctx.lineTo(x + size * 0.8, y - size * 0.66);
  ctx.lineTo(x + size * 0.65, y + size * 0.38);
  ctx.quadraticCurveTo(x + size * 0.3, y + size * 0.85, x, y + size);
  ctx.quadraticCurveTo(x - size * 0.3, y + size * 0.85, x - size * 0.65, y + size * 0.38);
  ctx.lineTo(x - size * 0.8, y - size * 0.66);
  ctx.closePath();
  ctx.fillStyle = '#dbe8b8';
  ctx.fill();
  ctx.strokeStyle = '#315e47';
  ctx.lineWidth = 2;
  ctx.stroke();
  line(ctx, [x - size * 0.35, y, x - size * 0.05, y + size * 0.3, x + size * 0.4, y - size * 0.35], '#315e47', 2);
}

/** Critical cues are drawn AFTER the world in CSS pixels, independent of camera/DPR scale. */
export class TrailAnnotations {
  private slots = new Map<string, number>();
  private layoutKey = '';
  private mapPlacement = 0;
  records: AnnotationRecord[] = [];
  /** The trail map drawn this frame (read-only diagnostics), or null when no slot was clear. */
  mapRect: Rect | null = null;
  /** Label accounting for the last frame (read-only diagnostics). */
  stats: LabelStats = { dense: false, callouts: 0, bodyBadges: 0, droppedByBudget: 0, droppedBySpace: 0, budget: null };

  reset(): void { this.slots.clear(); this.records = []; this.mapPlacement = 0; this.mapRect = null; }

  /** The HUD chip's deterministic slot: bottom-left of the view, just above the shell's footer bar,
   * away from the trail map (top band) and from the edge cues, which are placed after it. */
  hudRect(ctx: CanvasRenderingContext2D, view: CameraView, chip: HudChip): Rect {
    ctx.save();
    ctx.font = HUD_FONT;
    const w = Math.round(ctx.measureText(chip.label).width + 26);
    ctx.restore();
    return { x: 8, y: view.height - HUD_CHIP_HEIGHT - 8, w, h: HUD_CHIP_HEIGHT };
  }

  /**
   * `reserved` lists screen rectangles another layer (the cooperative inset, a reset chip) will
   * paint after this pass. Callouts, edge arrows and the trail map avoid them.
   * `chips` are the cooperative beat's contributor chips, placed like any other callout
   * (after the local tag and danger cues) so they never displace or cover them.
   * `hud` is the persistent counter chip; it takes its corner first, so every other cue avoids it.
   * `showHints` reveals the authored in-world `level.signs` cards. They are opt-in instructional
   * copy, not gameplay feedback, so the default view stays clear of them; every other callout here
   * (identity, counters, hazard state, danger cues, cargo, the trail map, contributor chips) is
   * always drawn.
   */
  draw(
    ctx: CanvasRenderingContext2D, level: LevelDef, world: WorldState, players: PlayerState[],
    view: CameraView, localId: string | null, now: number, reducedMotion: boolean,
    reserved: readonly Rect[] = [], chips: readonly HelpedChip[] = [], hud: HudChip | null = null,
    showHints = false,
  ): void {
    this.records = [];
    const layoutKey = `${view.width}:${view.height}:${view.follow}`;
    if (layoutKey !== this.layoutKey) { this.slots.clear(); this.layoutKey = layoutKey; }
    // A dense frame: the whole-trail overview, or any frame drawing bodies at ≤ 34 CSS px. Identity goes
    // on the bodies, contributor chips collapse per plate, no leaders, and a callout budget applies.
    const dense = !view.follow || PLAYER_W * view.scale <= DENSE_BODY_PX;
    const budget = dense ? DENSE_LABEL_BUDGET : null;
    const stats: LabelStats = { dense, callouts: 0, bodyBadges: 0, droppedByBudget: 0, droppedBySpace: 0, budget };
    this.stats = stats;
    const active = players.filter(player => player.connected && !player.entered);
    const bodies = active.map(player => ({ id: player.id, rect: screenRect(view, { ...player, w: PLAYER_W, h: PLAYER_H }) }));
    const obstacles: Rect[] = bodies.filter(body => visible(body.rect, view)).map(body => inflate(body.rect, 3));
    const external: Rect[] = reserved.map(rect => ({ ...rect }));
    const hudRect = hud ? this.hudRect(ctx, view, hud) : null;
    if (hudRect) external.push(inflate(hudRect, 2));
    const uncollected = level.keys.filter(key => !world.collectedKeys.includes(key.id));
    const keyScale = clamp(view.scale, 1, 1.4);
    const keyBounds = uncollected.map(key => {
      const point = toScreen(view, { x: key.x + key.w / 2, y: key.y + key.h / 2 });
      return { x: point.x - 21 * keyScale, y: point.y - 24 * keyScale, w: 42 * keyScale, h: 48 * keyScale };
    });
    const receivers = level.hazards.filter(hazard => describeHazard(hazard, level, world).held).map(hazard => screenRect(view, { ...hazard, h: 36 }));
    const mapOptions = [view.width - 162, 8, (view.width - 154) / 2].map(x => ({ x, y: 8, w: 154, h: 100 }));
    const mapFits = (rect: Rect) => [...obstacles, ...keyBounds, ...receivers, ...external].every(body => !intersects(rect, body, 4));
    let mapRect: Rect | null = null;
    if (view.follow) {
      if (mapFits(mapOptions[this.mapPlacement])) mapRect = mapOptions[this.mapPlacement];
      else {
        const index = mapOptions.findIndex(mapFits);
        if (index >= 0) { this.mapPlacement = index; mapRect = mapOptions[index]; }
      }
    }
    // In an exceptionally crowded top band the whole-trail control still gives full
    // context. Hiding the inset is preferable to covering a courier or the shared key.
    this.mapRect = mapRect;
    const mapReserved: Rect[] = mapRect ? [mapRect] : [];
    const occupied = [...obstacles, ...mapReserved, ...receivers, ...external];
    const cues: Cue[] = [];
    const local = players.find(player => player.id === localId);
    const localPoint = local ? { x: local.x + PLAYER_W / 2, y: local.y + PLAYER_H / 2 } : { x: WORLD_W / 2, y: 500 };
    const keys = level.keys.filter(key => !world.collectedKeys.includes(key.id));

    // The key has a high-contrast, fixed-size silhouette. Its centre is still the real pickup.
    for (const key of keys) {
      const point = toScreen(view, { x: key.x + key.w / 2, y: key.y + key.h / 2 });
      const keyRect = { x: point.x - 18 * keyScale, y: point.y - 21 * keyScale, w: 36 * keyScale, h: 42 * keyScale };
      if (visible(keyRect, view) && point.x >= 12 && point.x <= view.width - 12 && point.y > 12 && point.y < view.height - 12) {
        const bob = reducedMotion ? 0 : Math.sin(now / 370) * 1.5;
        ctx.save();
        disc(ctx, point.x, point.y + bob, 18 * keyScale, '#fff2ca');
        ctx.strokeStyle = '#aa792c';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        drawKey(ctx, point.x, point.y + bob, 1.18 * keyScale, -0.26);
        ctx.restore();
        occupied.push(keyRect);
        cues.push({ id: `key:${key.id}`, kind: 'key', label: 'SHARED KEY', anchor: point, width: 96, height: 24, fill: '#ffe4a1' });
      } else {
        const edge = edgePoint(point, view);
        cues.push({ id: `key:${key.id}`, kind: 'key', label: `KEY ${edge.arrow}`, anchor: edge.point, edge: edge.edge, offscreen: true, width: 72, height: 26, fill: '#ffe0a0' });
      }
    }

    const frame = visibleFrame(view);
    const badges: { id: string; body: Rect; rect: Rect; label: string; local: boolean; player: PlayerState }[] = [];
    for (const player of active) {
      const body = bodies.find(candidate => candidate.id === player.id)!.rect;
      const point = { x: body.x + body.w / 2, y: body.y + body.h / 2 };
      // Edge arrows are for bodies OUTSIDE the frame. A body standing at the world's edge (its rect
      // touching the frame border) or clipped by less than 60 % is on screen and keeps its badge.
      const onScreen = shownFraction(body, frame) >= ON_SCREEN_FRACTION;
      const isLocal = player.id === localId;
      const color = LANTERN_COLORS[player.color % 8];
      if (onScreen) {
        if (dense) {
          // A dense frame puts the identity ON the body: one fixed-size badge over the enamel badge's
          // place (the lower vessel), never a detached bubble on a leader. The local courier keeps its
          // YOU tag as well. Stacked bodies are sorted out below so both badges stay visible.
          const r = DENSE_BADGE_RADIUS;
          const rect = { x: point.x - r, y: body.y + body.h * 0.77 - r, w: r * 2, h: r * 2 };
          badges.push({ id: isLocal ? `badge:${player.id}` : `player:${player.id}`, body, rect, label: String(player.color + 1), local: isLocal, player });
          if (!isLocal) continue;
        } else if (!isLocal && view.scale >= 1.65) {
          // At action scale the physical enamel badge is the identifier. Duplicating
          // it in a detached bubble would make the diagram louder than the body.
          this.records.push({ id: `player:${player.id}`, kind: 'player', label: String(player.color + 1),
            rect: screenRect(view, { x: player.x + 9.5, y: player.y + 23.3, w: 13, h: 13 }), anchor: point,
            fontSize: 10.4 * view.scale, badgeSize: 12.2 * view.scale, offscreen: false, placement: 'body', leaderPx: 0 });
          stats.bodyBadges++;
          continue;
        }
        cues.push({ id: `player:${player.id}`, kind: isLocal ? 'local' : 'player', label: isLocal ? `YOU ${player.color + 1}` : String(player.color + 1),
          anchor: point, body, local: isLocal, badge: !isLocal, width: isLocal ? 62 : 20, height: isLocal ? 24 : 20, fill: isLocal ? INK : color, ink: isLocal ? PAPER : INK });
      } else {
        const edge = edgePoint(point, view);
        cues.push({ id: `player:${player.id}`, kind: isLocal ? 'local' : 'player', label: isLocal ? `${edge.arrow} YOU ${player.color + 1}` : `${edge.arrow} ${player.color + 1}`,
          anchor: edge.point, edge: edge.edge, offscreen: true, local: isLocal, width: isLocal ? 80 : 38, height: 24, fill: isLocal ? INK : color, ink: isLocal ? PAPER : INK });
      }
    }
    // Stacked couriers keep both badges legible: lower bodies first; an upper badge that would touch the
    // badge of a body beneath it moves up (within its own body) until clear.
    badges.sort((a, b) => b.body.y - a.body.y);
    for (let i = 0; i < badges.length; i++) {
      const badge = badges[i];
      for (let pass = 0; pass < 4; pass++) {
        const hit = badges.slice(0, i).find(lower => lower.body.y > badge.body.y && intersects(badge.rect, lower.rect, 1));
        if (!hit) break;
        badge.rect.y = Math.max(badge.body.y, hit.rect.y - badge.rect.h - 1);
        if (badge.rect.y <= badge.body.y) break;
      }
    }

    // Contributor chips ride with the body they credit; a body off the frame keeps its edge arrow only.
    ctx.font = '800 12px "Trebuchet MS", "Avenir Next", system-ui, sans-serif';
    if (dense) {
      // One chip per plate over the bodies it counted ("B ✓ 8 HELPED"), instead of one per body.
      const groups = new Map<string, { chips: HelpedChip[]; rects: Rect[] }>();
      for (const chip of chips) {
        if (chip.alpha <= 0.02) continue;
        const body = bodies.find(candidate => candidate.id === chip.playerId);
        if (!body || !visible(body.rect, view)) continue;
        const key = chip.plateId ?? `chip:${chip.playerId}`;
        const group = groups.get(key) ?? { chips: [], rects: [] };
        group.chips.push(chip); group.rects.push(body.rect);
        groups.set(key, group);
      }
      for (const [key, group] of groups) {
        const x0 = Math.min(...group.rects.map(r => r.x)), y0 = Math.min(...group.rects.map(r => r.y));
        const body = { x: x0, y: y0, w: Math.max(...group.rects.map(r => r.x + r.w)) - x0, h: Math.max(...group.rects.map(r => r.y + r.h)) - y0 };
        const letter = group.chips[0].letter ?? group.chips[0].label.charAt(0);
        const mine = group.chips.some(chip => chip.playerId === localId);
        const count = group.chips.length;
        const label = `${letter} ✓ ${count > 1 ? `${count} HELPED` : mine ? 'YOU HELPED' : 'HELPED'}`;
        cues.push({ id: `helped:${key}`, kind: 'helped', label, anchor: { x: body.x + body.w / 2, y: body.y + body.h / 2 }, body,
          width: Math.round(ctx.measureText(label).width + 16), height: 22, fill: '#f6dd9a', alpha: Math.max(...group.chips.map(chip => chip.alpha)) });
      }
    } else {
      for (const chip of chips) {
        if (chip.alpha <= 0.02) continue;
        const body = bodies.find(candidate => candidate.id === chip.playerId);
        if (!body || !visible(body.rect, view)) continue;
        cues.push({ id: `helped:${chip.playerId}`, kind: 'helped', label: chip.label, anchor: { x: body.rect.x + body.rect.w / 2, y: body.rect.y + body.rect.h / 2 }, body: body.rect,
          width: Math.round(ctx.measureText(chip.label).width + 16), height: 22, fill: '#f6dd9a', alpha: chip.alpha });
      }
    }

    const connected = world.players.filter(player => player.connected);
    const remaining = connected.filter(player => !player.entered).length;
    const doorRect = screenRect(view, level.exit);
    const doorPoint = toScreen(view, { x: level.exit.x + level.exit.w / 2, y: level.exit.y });
    if (visible(doorRect, view)) {
      cues.push({ id: 'exit', kind: 'exit', label: remaining ? `${remaining} TO GO` : 'ALL HOME', anchor: { x: doorPoint.x, y: doorPoint.y - 4 }, width: 94, height: 28, fill: '#fff1c8' });
    } else if (!keys.length) {
      const edge = edgePoint(doorPoint, view);
      cues.push({ id: 'exit', kind: 'exit', label: `HOME ${edge.arrow} · ${remaining}`, anchor: edge.point, edge: edge.edge, offscreen: true, width: 108, height: 28, fill: '#ffe0a0' });
    }

    level.switches.forEach((plate, index) => {
      const state = world.switches.find(candidate => candidate.id === plate.id);
      const rect = screenRect(view, plate);
      const point = toScreen(view, { x: plate.x + plate.w / 2, y: plate.y + plate.h });
      const label = `${String.fromCharCode(65 + index)}  ${state?.count ?? 0}/${state?.required ?? plate.required}${state?.latched ? ' ✓' : ''}`;
      if (visible(rect, view)) {
        cues.push({ id: `switch:${plate.id}`, kind: 'switch', label, anchor: point, below: true, width: state?.latched ? 82 : 66, height: 25, fill: state?.active ? '#f0c467' : PAPER });
      } else if (Math.abs(plate.x + plate.w / 2 - localPoint.x) < 430) {
        const edge = edgePoint(point, view);
        cues.push({ id: `switch:${plate.id}`, kind: 'switch', label: `${label} ${edge.arrow}`, anchor: edge.point, edge: edge.edge, offscreen: true, width: state?.latched ? 104 : 88, height: 25, fill: state?.active ? '#f0c467' : PAPER });
      }
    });

    const platforms = getPlatforms(level, world);
    const destinations = [
      ...level.gates.map(gate => ({ ...gate, open: isGateOpen(gate, world) })),
      ...level.platforms.filter(platform => platform.switchIds?.length).map(platform => ({
        ...(platforms.find(current => current.id === platform.id) ?? platform), switchIds: platform.switchIds!, open: platforms.some(current => current.id === platform.id),
      })),
    ];
    for (const destination of destinations) {
      if (!visible(screenRect(view, destination), view)) continue;
      const letters = destination.switchIds.map(id => String.fromCharCode(65 + level.switches.findIndex(plate => plate.id === id))).join('+');
      const point = toScreen(view, { x: destination.x + destination.w / 2, y: destination.y + destination.h });
      cues.push({ id: `circuit:${destination.id}`, kind: 'circuit', label: `${letters}${destination.open ? ' ✓' : ''}`, anchor: point, below: true,
        width: Math.max(26, letters.length * 9 + (destination.open ? 29 : 17)), height: 23, fill: destination.open ? '#eac467' : PAPER });
    }
    for (const crate of world.crates) {
      if (!visible(screenRect(view, crate), view)) continue;
      cues.push({ id: `crate:${crate.id}`, kind: 'cargo', label: `CARGO · ${crate.weight ?? 2}`, anchor: toScreen(view, { x: crate.x + crate.w / 2, y: crate.y }), width: 88, height: 24, fill: '#f3dfb8' });
    }

    for (const hazard of level.hazards) {
      if (!hazard.cycle && !hazard.cycleAfterKey) continue;
      const rect = screenRect(view, hazard);
      if (!visible(rect, view)) continue;
      const state = describeHazard(hazard, level, world);
      const anchor = { x: clamp(rect.x + rect.w / 2, 40, view.width - 40), y: clamp(rect.y - 4, 24, view.height - 24) };
      cues.push({ id: `hazard:${hazard.id}`, kind: 'hazard', label: state.label, anchor, width: state.held ? 118 : state.keyLocked ? 96 : 106, height: 27,
        icon: state.held ? 'shield' : state.keyLocked ? 'lock' : undefined,
        fill: state.held || (!state.active && !state.warning) ? '#dae9b9' : state.warning ? '#ffe09a' : '#f7c9a4' });
    }

    // A deadly surface the local courier could fall into, lying just outside the frame,
    // keeps a persistent edge marker: waves (water) or thorns (bramble) with an arrow
    // toward it. It disappears only once the surface itself is in view.
    if (local && local.connected && !local.entered) {
      const zones = dangerZones(level, platforms, world, { x: local.x, y: local.y, w: PLAYER_W, h: PLAYER_H }, DANGER_CUE_RANGE);
      const shown = (zone: typeof zones[number]) => {
        const band = screenRect(view, { x: zone.span[0], y: zone.surface, w: zone.span[1] - zone.span[0], h: DANGER_SURFACE });
        const shownW = Math.min(band.x + band.w, view.width) - Math.max(band.x, 0);
        const shownH = Math.min(band.y + band.h, view.height) - Math.max(band.y, 0);
        return shownW >= 24 && shownH >= 8;
      };
      const cued = new Set<string>();
      for (const zone of zones) {
        // One marker per hazard, for its nearest span, and none once any part of that
        // surface is on screen: the water itself is then the warning.
        if (cued.has(zone.hazardId) || zones.some(other => other.hazardId === zone.hazardId && shown(other))) continue;
        cued.add(zone.hazardId);
        const point = toScreen(view, { x: clamp(localPoint.x, zone.span[0], zone.span[1]), y: zone.surface + 6 });
        const edge = edgePoint(point, view);
        const lower = point.y > view.height / 2;
        const arrow = edge.edge === 'bottom' ? '↓' : edge.edge === 'right' ? lower ? '↘' : '→' : edge.edge === 'left' ? lower ? '↙' : '←' : '↑';
        cues.push({ id: `danger:${zone.id}`, kind: 'danger', label: `${zone.kind === 'water' ? 'WATER' : 'THORNS'} ${arrow}`, anchor: edge.point, edge: edge.edge,
          offscreen: true, width: 96, height: 26, fill: zone.kind === 'water' ? '#3d7a82' : '#7a4e3e', ink: PAPER, danger: zone.kind });
      }
    }

    // In-world signs: the whole-trail overview shows them all (unless the frame is too small); the
    // Team view shows the ones whose anchor lies inside the frame, as the same paper cards anchored
    // in world space. They are placed last, so the local courier, YOU tag, danger chips, trail map,
    // bodies and every other callout are already occupied and a card nudges up rather than cover them.
    if (showHints && (view.follow || !view.compact)) {
      const changedRequirement = level.switches.some(plate => {
        const state = world.switches.find(candidate => candidate.id === plate.id);
        return state?.required !== undefined && state.required !== plate.required;
      });
      const phone = view.follow && view.width < 500;
      for (const [index, sign] of level.signs.entries()) {
        if (sign.icon === 'home') continue;
        // Authored numeric hints are not live quorum displays. Yield to the effective
        // counted plate and accessible status when the connected roster changes.
        if (changedRequirement && /\d/.test(sign.text)) continue;
        const point = toScreen(view, { x: sign.icon === 'stack' ? sign.x - 105 : sign.x, y: sign.y - (sign.icon === 'stack' ? 72 : 36) });
        if (view.follow && (point.x < frame.x - 24 || point.x > frame.x + frame.w + 24 || point.y < frame.y - 24 || point.y > frame.y + frame.h + 24)) continue;
        ctx.font = '600 12px "Trebuchet MS", system-ui, sans-serif';
        const single = ctx.measureText(sign.text).width;
        // Portrait phones wrap a long sign onto two rows so two cards still fit a 364-px frame.
        const lines = phone && single > 112 ? wrapSign(ctx, sign.text) : null;
        const width = lines ? Math.max(...lines.map(line => ctx.measureText(line).width)) + 20 : Math.min(238, single + 20);
        cues.push({ id: `sign:${index}`, kind: 'sign', label: sign.text, lines: lines ?? undefined, anchor: point, width: Math.round(width), height: lines ? 40 : 26, fill: '#fff4dc', optional: true });
      }
    }

    // Dense frames rank by what a viewer must not lose at 30-px bodies: the local tag, deadly surfaces and hazard
    // timing, the plate counters, the objectives (door, key, off-frame couriers), then the changed routes, cargo,
    // the sign cards, and last the (collapsed) contributor chips. The close Team view keeps its previous order.
    const priority = dense
      ? (cue: Cue) => cue.local ? 0 : cue.kind === 'danger' ? 0.5 : cue.kind === 'hazard' ? 1 : cue.kind === 'switch' ? 2 : cue.kind === 'exit' ? 3 : cue.kind === 'key' ? 3.2
        : cue.kind === 'player' ? 3.5 : cue.kind === 'circuit' ? 4 : cue.kind === 'cargo' ? 4.5 : cue.kind === 'sign' ? 5 : cue.kind === 'helped' ? 6 : 4.8
      : (cue: Cue) => cue.local ? 0 : cue.kind === 'danger' ? 0.5 : cue.kind === 'helped' ? 0.7 : cue.kind === 'player' ? 1 : cue.kind === 'hazard' ? 2 : cue.kind === 'switch' || cue.kind === 'circuit' ? 3 : cue.kind === 'key' ? 4 : cue.kind === 'sign' ? 6 : 5;
    cues.sort((a, b) => priority(a) - priority(b) || a.anchor.y - b.anchor.y || a.id.localeCompare(b.id));
    const placed: { cue: Cue; rect: Rect }[] = [];
    for (const cue of cues) {
      if (budget !== null && placed.length >= budget) { stats.droppedByBudget++; continue; }
      const placement = this.place(cue, occupied, view, bodies, dense);
      if (!placement) { stats.droppedBySpace++; continue; }
      const { rect, index } = placement;
      occupied.push(inflate(rect, 2));
      this.slots.set(`${cue.id}:${cue.edge ?? 'onscreen'}`, index);
      placed.push({ cue, rect });
      this.records.push({ id: cue.id, kind: cue.kind, label: cue.label, rect, anchor: cue.anchor, fontSize: 12, badgeSize: cue.kind === 'player' || cue.kind === 'local' ? Math.min(rect.h, 20) : 0, offscreen: !!cue.offscreen, placement: 'callout',
        leaderPx: hasLeader(cue, dense) ? leaderLength(cue, rect) : 0 });
    }
    stats.callouts = placed.length;
    // Badges on bodies are painted first (they belong to the bodies), callouts over them.
    for (const badge of badges) {
      this.paintBadge(ctx, badge.rect, badge.label, badge.local);
      this.records.push({ id: badge.id, kind: 'player', label: badge.label, rect: { ...badge.rect }, anchor: { x: badge.body.x + badge.body.w / 2, y: badge.body.y + badge.body.h / 2 },
        fontSize: 11, badgeSize: badge.rect.h, offscreen: false, placement: 'body', leaderPx: 0 });
      stats.bodyBadges++;
    }
    for (const { cue, rect } of placed) this.paint(ctx, cue, rect, true, now, reducedMotion, dense);
    for (const { cue, rect } of placed) this.paint(ctx, cue, rect, false, now, reducedMotion, dense);
    if (mapRect) this.map(ctx, level, world, players, localId, view, mapRect);
    if (hud && hudRect) {
      this.paintHud(ctx, hud, hudRect);
      this.records.push({ id: hud.id, kind: 'hud', label: hud.label, rect: hudRect, anchor: { x: hudRect.x + hudRect.w / 2, y: hudRect.y + hudRect.h / 2 },
        fontSize: 12, badgeSize: 0, offscreen: false, placement: 'callout', leaderPx: 0 });
    }
  }

  /** Styled like the other paper chips; a small amber dot marks it as a live counter. */
  private paintHud(ctx: CanvasRenderingContext2D, chip: HudChip, rect: Rect): void {
    ctx.save();
    rounded(ctx, rect.x, rect.y + 1.5, rect.w, rect.h, 7);
    ctx.fillStyle = 'rgba(31,49,39,.14)';
    ctx.fill();
    rounded(ctx, rect.x, rect.y, rect.w, rect.h, 7);
    ctx.fillStyle = '#fff6e4';
    ctx.fill();
    ctx.strokeStyle = '#51684f';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    disc(ctx, rect.x + 9, rect.y + rect.h / 2, 3, AMBER);
    text(ctx, chip.label, rect.x + 17, rect.y + rect.h / 2 + 0.3, 12, INK, 'left', 800);
    ctx.restore();
  }

  /** The dense frame's identity badge: the lantern's own enamel badge, at a fixed legible size. */
  private paintBadge(ctx: CanvasRenderingContext2D, rect: Rect, label: string, local: boolean): void {
    const cx = rect.x + rect.w / 2, cy = rect.y + rect.h / 2, r = rect.w / 2;
    ctx.save();
    disc(ctx, cx, cy + 1, r, 'rgba(31,49,39,.22)');
    disc(ctx, cx, cy, r, INK);
    ctx.beginPath();
    ctx.arc(cx, cy, r + 0.3, 0, TAU);
    ctx.strokeStyle = local ? '#fff5c6' : 'rgba(255,251,225,.78)';
    ctx.lineWidth = local ? 1.6 : 1;
    ctx.stroke();
    text(ctx, label, cx, cy + 0.5, 11, PAPER, 'center', 900);
    ctx.restore();
  }

  private place(cue: Cue, occupied: Rect[], view: CameraView, bodies: { id: string; rect: Rect }[], dense = false): { rect: Rect; index: number } | null {
    const { width: w, height: h, anchor: p, body } = cue;
    const positions: Point[] = [];
    if (cue.offscreen) {
      for (const shift of [0, -28, 28, -56, 56, -84, 84, -112, 112, -140, 140]) {
        if (cue.edge === 'left' || cue.edge === 'right') positions.push({ x: cue.edge === 'left' ? 8 : view.width - w - 8, y: p.y - h / 2 + shift });
        else positions.push({ x: p.x - w / 2 + shift, y: cue.edge === 'top' ? 8 : view.height - h - 8 });
      }
    } else if (cue.kind === 'sign' && view.follow) {
      // A Team-view sign card sits above its anchor and nudges UP (then a little sideways) away from
      // bodies and other cards, never down onto the ground the courier walks on.
      for (const dy of [0, 28, 56]) for (const dx of [0, -40, 40, -80, 80]) positions.push({ x: p.x - w / 2 + dx, y: p.y - h - 10 - dy });
    } else if (body) {
      positions.push({ x: p.x - w / 2, y: body.y - h - 8 });
      // A collapsed contributor chip spans a group: above it, aligned to either end, before beside it.
      if (dense && cue.kind === 'helped' && body.w > w / 2) positions.push({ x: body.x + body.w - w, y: body.y - h - 8 }, { x: body.x, y: body.y - h - 8 });
      positions.push(
        { x: body.x - w - 10, y: p.y - h / 2 }, { x: body.x + body.w + 10, y: p.y - h / 2 },
        { x: p.x - w / 2, y: body.y - h * 2 - 14 },
        { x: body.x - w - 10, y: body.y - h - 6 }, { x: body.x + body.w + 10, y: body.y - h - 6 });
    } else {
      positions.push({ x: p.x - w / 2, y: cue.below ? p.y + 10 : p.y - h - 10 },
        { x: p.x - w / 2, y: cue.below ? p.y - h - 10 : p.y + 12 },
        { x: p.x - w - 16, y: p.y - h / 2 }, { x: p.x + 16, y: p.y - h / 2 });
      if (cue.below) positions.push({ x: p.x - w / 2, y: p.y + 5 });
    }
    // Only the local courier's own tag may wander: every other on-screen label (a ghost bridge's
    // chip, plate counters, cargo, the door count, the key, hazard state, a partner's number) stays
    // within a short leader of what it labels or is not drawn this frame. A label tethered to a
    // frame corner by a long line reads as a pointer to something else entirely.
    const near = !cue.offscreen && !cue.local;
    for (const radius of near ? [40, 68, 96] : [40, 68, 96, 128, 164, 202]) {
      for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [-1, -0.65], [1, -0.65], [0, 1], [-1, 0.65], [1, 0.65]]) {
        positions.push({ x: p.x - w / 2 + dx * radius, y: p.y - h / 2 + dy * radius });
      }
    }
    const last = this.slots.get(`${cue.id}:${cue.edge ?? 'onscreen'}`);
    const order = positions.map((position, index) => ({ position, index }));
    // Keep a nearby valid slot stable, but reclaim the near-anchor slot after a crowd
    // clears. A distant fallback from a prior frame must not become a permanent tether.
    const primaryCount = cue.offscreen ? 11 : cue.kind === 'sign' && view.follow ? 15 : body ? dense && cue.kind === 'helped' && body.w > w / 2 ? 8 : 6 : 4;
    if (last !== undefined && last >= 0 && last < primaryCount && order[last]) order.unshift(order.splice(last, 1)[0]);
    for (const { position, index } of order) {
      const rect = { ...position, w, h };
      if (rect.x < 6 || rect.y < 6 || rect.x + w > view.width - 6 || rect.y + h > view.height - 6) continue;
      if (cue.optional && Math.hypot(rect.x + w / 2 - p.x, rect.y + h / 2 - p.y) > (cue.kind === 'sign' && view.follow ? 108 : 80)) continue;
      if (near && (hasLeader(cue) || cue.kind === 'helped') && leaderLength(cue, rect) > LEADER_MAX_PX) continue;
      if (occupied.some(obstacle => intersects(rect, obstacle, 1))) continue;
      if (!cue.offscreen && !dense && cue.kind !== 'sign' && cue.kind !== 'helped') {
        // A drawn leader never crosses another courier (a dense frame draws none).
        const end = leaderEnd(cue, rect);
        const start = { x: rect.x + w / 2, y: rect.y + h / 2 };
        if (bodies.some(body => `player:${body.id}` !== cue.id && crossesBody(start, end, body.rect))) continue;
      }
      return { rect, index };
    }
    if (cue.optional || near) return null;
    // Dense overview fallback for the local courier's own tag: scan actual free screen space rather than hiding identity.
    for (let y = 8; y + h < view.height - 6; y += h + 6) {
      for (let x = 8; x + w < view.width - 6; x += 24) {
        const rect = { x, y, w, h };
        if (!occupied.some(obstacle => intersects(rect, obstacle, 1))) return { rect, index: -1 };
      }
    }
    return null;
  }

  private paint(ctx: CanvasRenderingContext2D, cue: Cue, rect: Rect, leader: boolean, now: number, reducedMotion: boolean, dense = false): void {
    const center = { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
    const anchor = leaderEnd(cue, rect);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (cue.alpha !== undefined) ctx.globalAlpha = clamp(cue.alpha, 0, 1);
    if (leader && hasLeader(cue, dense)) {
      line(ctx, [center.x, center.y, anchor.x, anchor.y], PAPER, cue.local ? 4 : 2.8);
      line(ctx, [center.x, center.y, anchor.x, anchor.y], cue.local ? INK : '#64785f', cue.local ? 1.8 : 1);
      disc(ctx, anchor.x, anchor.y, cue.local ? 2.4 : 1.6, cue.local ? AMBER : INK);
    }
    if (leader) { ctx.restore(); return; }
    rounded(ctx, rect.x, rect.y + 1.5, rect.w, rect.h, cue.badge ? 10 : 7);
    ctx.fillStyle = 'rgba(31,49,39,.14)';
    ctx.fill();
    if (cue.danger) {
      // A pale outer ring keeps the dark marker distinct from soil and shadow behind it.
      rounded(ctx, rect.x - 1.5, rect.y - 1.5, rect.w + 3, rect.h + 3, 8.5);
      ctx.fillStyle = 'rgba(255,246,223,.9)';
      ctx.fill();
    }
    rounded(ctx, rect.x, rect.y, rect.w, rect.h, cue.badge ? 10 : 7);
    ctx.fillStyle = cue.fill;
    ctx.fill();
    ctx.strokeStyle = cue.local ? '#e1b967' : cue.kind === 'sign' ? '#a6ad8c' : cue.kind === 'helped' ? '#b98a2f' : INK;
    ctx.lineWidth = cue.local || cue.badge || cue.danger ? 1.8 : cue.kind === 'helped' ? 1.5 : 1.3;
    ctx.stroke();
    if (cue.danger) this.dangerMark(ctx, cue.danger, { x: rect.x + 4, y: rect.y + 4, w: 24, h: rect.h - 8 }, now, reducedMotion);
    if (cue.icon === 'shield') shield(ctx, rect.x + 13, center.y, 7.5);
    if (cue.icon === 'lock') {
      rounded(ctx, rect.x + 8, center.y - 1, 10, 8, 2); ctx.fillStyle = INK; ctx.fill();
      ctx.beginPath(); ctx.arc(rect.x + 13, center.y - 1, 3.5, Math.PI, 0); ctx.strokeStyle = INK; ctx.lineWidth = 1.6; ctx.stroke();
      disc(ctx, rect.x + 13, center.y + 3, 1, PAPER);
    }
    if (cue.lines) cue.lines.forEach((row, index) => text(ctx, row, center.x, center.y + 0.3 + (index - (cue.lines!.length - 1) / 2) * 14, 12, cue.ink ?? INK, 'center', 800));
    else text(ctx, cue.label, center.x + (cue.icon ? 8 : cue.danger ? 13 : 0), center.y + 0.3, 12, cue.ink ?? INK, 'center', cue.badge || cue.local || cue.danger ? 900 : 800);
    ctx.restore();
  }

  /** Waves for water, thorns for bramble: the marker reads as the surface it points at. */
  private dangerMark(ctx: CanvasRenderingContext2D, kind: 'water' | 'bramble', box: Rect, now: number, reducedMotion: boolean): void {
    ctx.save();
    rounded(ctx, box.x, box.y, box.w, box.h, 4);
    ctx.fillStyle = kind === 'water' ? '#5d9aa0' : '#9a6a55';
    ctx.fill();
    ctx.clip();
    if (kind === 'water') {
      const phase = reducedMotion ? 0 : now / 260;
      for (const [offset, color, width] of [[-4, 'rgba(232,247,240,.95)', 1.6], [1, 'rgba(24,60,64,.55)', 1.2], [5.5, 'rgba(232,247,240,.75)', 1.4]] as const) {
        ctx.beginPath();
        for (let x = box.x - 2; x <= box.x + box.w + 2; x += 2) {
          const y = box.y + box.h / 2 + offset + Math.sin(x * 0.55 + phase) * 1.7;
          if (x === box.x - 2) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = color; ctx.lineWidth = width; ctx.lineCap = 'round'; ctx.stroke();
      }
    } else {
      for (let x = box.x - box.h; x < box.x + box.w + box.h; x += 7) {
        line(ctx, [x, box.y + box.h, x + box.h, box.y], 'rgba(255,229,190,.55)', 2.2);
      }
      for (let x = box.x + 3; x < box.x + box.w - 2; x += 6) line(ctx, [x, box.y + box.h - 1, x + 2.5, box.y + box.h - 6, x + 5, box.y + box.h - 1], '#3d2a22', 1.4);
    }
    ctx.restore();
    rounded(ctx, box.x, box.y, box.w, box.h, 4);
    ctx.strokeStyle = 'rgba(255,246,223,.85)'; ctx.lineWidth = 1; ctx.stroke();
  }

  private map(ctx: CanvasRenderingContext2D, level: LevelDef, world: WorldState, players: PlayerState[], localId: string | null, view: CameraView, rect: Rect): void {
    const { x, y, w, h } = rect;
    ctx.save();
    rounded(ctx, x, y, w, h, 8);
    ctx.fillStyle = '#fff6e4';
    ctx.fill();
    ctx.strokeStyle = '#51684f';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    text(ctx, 'TRAIL MAP', x + 10, y + 14, 12, INK, 'left', 800);
    disc(ctx, x + w - 14, y + 14, 3.5, AMBER);
    const map = { x: x + 9, y: y + 27, w: w - 18, h: 64 };
    ctx.fillStyle = level.theme === 'night' ? '#5f7c75' : '#dce4cf';
    ctx.fillRect(map.x, map.y, map.w, map.h);
    const point = (p: Point) => ({ x: map.x + p.x / WORLD_W * map.w, y: map.y + p.y / WORLD_H * map.h });
    ctx.save();
    ctx.beginPath();
    ctx.rect(map.x, map.y, map.w, map.h);
    ctx.clip();
    for (const platform of getPlatforms(level, world)) {
      const p = point(platform);
      ctx.fillStyle = '#7a805e';
      ctx.fillRect(p.x, p.y, Math.max(1, platform.w / WORLD_W * map.w), Math.max(1.4, platform.h / WORLD_H * map.h));
    }
    for (const hazard of level.hazards) {
      const p = point(hazard);
      ctx.fillStyle = isHazardActive(hazard, world) ? '#b27651' : '#88b18c';
      ctx.fillRect(p.x, p.y, Math.max(2, hazard.w / WORLD_W * map.w), Math.max(2, hazard.h / WORLD_H * map.h));
    }
    for (const gate of level.gates) {
      if (isGateOpen(gate, world)) continue;
      const p = point(gate);
      line(ctx, [p.x, p.y, p.x, p.y + gate.h / WORLD_H * map.h], '#675735', 2);
    }
    for (const key of level.keys) {
      if (world.collectedKeys.includes(key.id)) continue;
      const p = point({ x: key.x + key.w / 2, y: key.y + key.h / 2 });
      disc(ctx, p.x, p.y, 3.5, '#ffd065');
      ctx.strokeStyle = '#624a24';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    const door = point({ x: level.exit.x + level.exit.w / 2, y: level.exit.y + level.exit.h / 2 });
    rounded(ctx, door.x - 3, door.y - 5, 6, 10, 3);
    ctx.fillStyle = '#f3d095';
    ctx.fill();
    ctx.strokeStyle = '#4a593d';
    ctx.lineWidth = 1;
    ctx.stroke();
    for (const player of players) {
      if (!player.connected || player.entered) continue;
      const p = point({ x: player.x + PLAYER_W / 2, y: player.y + PLAYER_H / 2 });
      disc(ctx, p.x, p.y, player.id === localId ? 3.8 : 2.2, LANTERN_COLORS[player.color % 8]);
      ctx.strokeStyle = player.id === localId ? '#fff9d7' : INK;
      ctx.lineWidth = player.id === localId ? 1.8 : 0.8;
      ctx.stroke();
    }
    const corner = point({ x: view.x, y: view.y });
    ctx.strokeStyle = '#fff8d3';
    ctx.lineWidth = 2.3;
    ctx.strokeRect(corner.x, corner.y, view.width / view.scale / WORLD_W * map.w, view.height / view.scale / WORLD_H * map.h);
    ctx.strokeStyle = '#665a35';
    ctx.lineWidth = 0.8;
    ctx.strokeRect(corner.x, corner.y, view.width / view.scale / WORLD_W * map.w, view.height / view.scale / WORLD_H * map.h);
    ctx.restore();
    ctx.restore();
  }
}
